import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { motion, useReducedMotion } from "framer-motion";

// Shared with ChecklistSteps, so both details ladders look and move alike
export const DOT = 16; // circle diameter (px)
export const LINE = 10; // connector between two levels
const GAP = 44; // the stretch towards the next level, with "x to go" beside it

// Seconds. The circles fill one by one up to your level, which then ripples,
// then the line towards the next level fills to your actual progress.
export const START = 0.2;
export const STEP = 0.12;
const POP = { type: "spring", stiffness: 500, damping: 18 };

// Level label | circle column | count, with the circles centred in the popup
const ROW_SX = {
  display: "grid",
  gridTemplateColumns: `1fr ${DOT}px 1fr`,
  columnGap: 1.5,
  alignItems: "center",
};

/**
 * A vertical connector, filling top-down to `fill` (0–1) after `delay`.
 * `rowSx` sets the ladder's columns (the circle column is the second).
 */
export function Connector({
  height,
  fill,
  delay,
  duration,
  rowSx = ROW_SX,
  children,
}) {
  return (
    <Box sx={rowSx}>
      <span />
      <Box
        sx={{
          position: "relative",
          justifySelf: "center",
          width: 2,
          height,
          bgcolor: "secondary.main",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {fill > 0 && (
          <Box
            component={motion.div}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: fill }}
            transition={{ delay, duration, ease: "easeOut" }}
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "primary.main",
              transformOrigin: "top",
            }}
          />
        )}
      </Box>
      {children ?? <span />}
    </Box>
  );
}

/**
 * A ladder circle: ticked once reached, otherwise an empty ring — grey, or
 * primary when `highlighted` (the next step). `hideTick` fills it without
 * the tick (a skipped checklist step: dealt with, but not done).
 * `ripple` sends two rings out from it, `delay` after which it settles.
 */
export function LevelDot({
  reached,
  ripple,
  highlighted,
  hideTick,
  delay,
  reduceMotion,
}) {
  return (
    <Box
      sx={{
        position: "relative",
        width: DOT,
        height: DOT,
        borderRadius: "50%",
        border: 2,
        borderColor: reached || highlighted ? "primary.main" : "text.disabled",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {reached && (
        <Box
          component={motion.div}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ ...POP, delay }}
          sx={{
            position: "absolute",
            inset: -2,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!hideTick && <CheckRoundedIcon sx={{ fontSize: 12 }} />}
        </Box>
      )}
      {/* Two rings ripple out (your level, or the next step) */}
      {ripple &&
        !reduceMotion &&
        [0, 0.18].map((offset) => (
          <Box
            key={offset}
            component={motion.div}
            initial={{ scale: 1, opacity: 0 }}
            animate={{ scale: [1, 2.6], opacity: [0.7, 0] }}
            transition={{ delay: delay + 0.15 + offset, duration: 0.7 }}
            sx={{
              position: "absolute",
              inset: -2,
              borderRadius: "50%",
              border: 2,
              borderColor: "primary.main",
              pointerEvents: "none",
            }}
          />
        ))}
    </Box>
  );
}

/**
 * A badge's levels in its details popover: "Level 1", 2–6, "Level 7" on the
 * left, the count each needs on the right, circles down the middle. Levels up
 * to yours are ticked; the line towards the next one is longer, fills to your
 * progress between the two, and gives your count ("319 movies finished"). Animates on every open
 * (the popover/dialog remounts its content); instant with reduced motion.
 */
export default function BadgeLevels({ row }) {
  const reduceMotion = useReducedMotion();
  const pace = reduceMotion ? 0 : 1;
  const levels = row.details.items;
  const held = row.level; // 0–7
  const { start = 0, current, target } = row.progress;
  const progress = row.isMaxed
    ? 0
    : Math.min(1, Math.max(0, (current - start) / (target - start || 1)));

  // When each level's circle fills; the stretch beyond fills after yours
  const dotDelay = (n) => (START + (n - 1) * STEP) * pace;
  const heldAt = held ? dotDelay(held) : START * pace;
  const gapDelay = heldAt + (held ? 0.2 : 0) * pace;

  return (
    <Box sx={{ py: 0.5 }}>
      {levels.map((item, index) => {
        const n = index + 1;
        const reached = n <= held;
        const isNext = !row.isMaxed && n === held + 1;
        const label = n === 1 || n === levels.length ? item.label : n;

        return (
          <Box key={item.key}>
            {isNext ? (
              <Connector
                height={GAP}
                fill={progress}
                delay={gapDelay}
                duration={0.3 * pace}
              >
                <Typography
                  component={motion.p}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: gapDelay + 0.15 * pace, duration: 0.25 }}
                  variant="caption"
                  color="text.secondary"
                >
                  {row.details.summary}
                </Typography>
              </Connector>
            ) : (
              index > 0 && (
                <Connector
                  height={LINE}
                  fill={reached ? 1 : 0}
                  delay={dotDelay(n) - 0.08 * pace}
                  duration={0.08 * pace}
                />
              )
            )}

            <Box sx={ROW_SX}>
              <Typography
                variant="body2"
                textAlign="right"
                color={reached ? "text.primary" : "text.disabled"}
                fontWeight={n === held ? "bold" : undefined}
              >
                {label}
              </Typography>
              <LevelDot
                reached={reached}
                ripple={n === held}
                delay={dotDelay(n)}
                reduceMotion={reduceMotion}
              />
              <Typography
                variant="body2"
                color={reached ? "text.secondary" : "text.disabled"}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {item.detail}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
